function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { useEffect, useCallback, useMemo } from 'react';
import Feature from 'ol/Feature';
import { v4 as uuidv4 } from 'uuid';
import { useOpenLayers } from '../react-ol';
import useGeometryStyles from '../hooks/useGeometryStyles';
import { useGeometryParse } from '../hooks/useGeometryParse';
import { useVectorLayer } from '../hooks/useVectorLayer';
import { CLEAR_FEATURES_KEY } from '../hooks/useClearFeatures';
import useApplicationState from '../hooks/useApplicationState';
import { useHighlightLayer } from '../hooks/useHighlightLayer';
import { useGeometryArea } from '../hooks/useGeometryArea';
var PRIMARY_FEATURE_PROPERTIES = ['geometry', 'reference', 'name', 'boundaryType'];

var getFeatureId = feature => {
  var featureId = feature.getId();

  if (featureId) {
    return featureId;
  }

  var id = uuidv4();
  feature.setId(id);
  return feature.getId();
};

var getFeatureProperties = (feature, getArea) => {
  var featureProperties = feature.getProperties();
  delete featureProperties.geometry;

  var properties = _objectSpread(_objectSpread({}, featureProperties), {}, {
    featureId: getFeatureId(feature)
  });

  var area = getArea(feature.getGeometry());
  return {
    area,
    properties
  };
};

var clearDefaultFeatureProperties = feature => {
  var featureProps = feature.getProperties();
  Object.keys(featureProps).forEach(key => {
    if (!PRIMARY_FEATURE_PROPERTIES.includes(key)) {
      feature.unset(key);
    }
  });
};
/**
 * Auto fills features from a given
 *
 * Select layer (ex: WFS layer) into a new layer
 *
 * On hover highlights select layer features on highlight layer
 *
 * Displays selected features vertices, area and line measurements
 *
 * Takes style prop which can be used to style
 *
 * Measurements, vertices of auto filled features
 */


var AutofillFeatures = _ref => {
  var {
    canSelect,
    id,
    label,
    reference,
    maxZoom,
    minZoom,
    zIndex,
    style,
    selectLayerReference,
    showAreaMeasurements = false,
    showLineMeasurements = false,
    showVertices = false,
    canMergeFeatures = true,
    canHighlightFeatures = true,
    boundaryType,
    highlightLayerZIndex,
    onSelect
  } = _ref;
  var {
    map
  } = useOpenLayers();
  var {
    autofillFeatures,
    setAutofillFeatures
  } = useApplicationState();
  var autofillLayer = useVectorLayer({
    id,
    label,
    reference,
    zIndex: zIndex !== null && zIndex !== void 0 ? zIndex : 15,
    maxZoom,
    minZoom
  });
  var {
    getStyles,
    highlightStyles
  } = useGeometryStyles({
    style,
    showVertices,
    showAreaMeasurements,
    showLineMeasurements,
    vectorLayer: autofillLayer
  });
  var currentBoundaryFeatures = useMemo(() => autofillFeatures.filter(f => f.get('boundaryType') === boundaryType), [autofillFeatures, boundaryType]);
  var {
    mergeGeometries,
    parser
  } = useGeometryParse();
  var {
    getGeometryArea
  } = useGeometryArea();
  var handleSetAutofillFeatures = useCallback(feature => {
    setAutofillFeatures(state => Array.from(new Set([...state, feature])));
  }, [setAutofillFeatures]);
  var getSelectedFeature = useCallback((pixel, closestFeatureToCoordinate) => {
    if (!map) return undefined;
    var featureList = map.getFeaturesAtPixel(pixel, {
      layerFilter: layer => layer.get('reference') === selectLayerReference
    });

    if (featureList.length > 1) {
      // If there are more multiple features present in selected area
      // Then get the feature closest to the coordinate
      return featureList.find(f => f === closestFeatureToCoordinate);
    } else {
      return map.forEachFeatureAtPixel(pixel, feature => feature, {
        layerFilter: layer => layer.get('reference') === selectLayerReference
      });
    }
  }, [map, selectLayerReference]);
  useHighlightLayer({
    canHighlightFeatures,
    sourceLayerId: selectLayerReference,
    style: highlightStyles,
    zIndex: highlightLayerZIndex,
    getSelectedFeature
  });
  useEffect(() => {
    autofillLayer.setStyle(getStyles);
  }, [autofillLayer, getStyles]); // On clear layer features, clear autofill features

  useEffect(() => {
    var handlePropertyChange = e => {
      var key = e.key;

      if (key === CLEAR_FEATURES_KEY && autofillLayer.get(key)) {
        setAutofillFeatures(() => []);
        autofillLayer.set(CLEAR_FEATURES_KEY, false);
      }
    };

    autofillLayer.on('propertychange', handlePropertyChange);
    return () => {
      autofillLayer.un('propertychange', handlePropertyChange);
    };
  }, [autofillLayer, setAutofillFeatures]);
  useEffect(() => {
    if (!map || !canSelect) return;
    var featureSelectLayer = map.getLayers().getArray().find(l => l.get('reference') === selectLayerReference);

    var onFeatureSelect = e => {
      var _featureSelectLayer$g;

      var coordinate = map.getCoordinateFromPixel(e.pixel);
      var closestFeatureToCordinate = featureSelectLayer === null || featureSelectLayer === void 0 ? void 0 : (_featureSelectLayer$g = featureSelectLayer.getSource()) === null || _featureSelectLayer$g === void 0 ? void 0 : _featureSelectLayer$g.getClosestFeatureToCoordinate(coordinate);
      var selectedFeature = getSelectedFeature(e.pixel, closestFeatureToCordinate);
      if (!selectedFeature) return;
      var hasBoundaryType = !!selectedFeature.get('boundaryType');
      var isSameBoundaryType = selectedFeature.get('boundaryType') === boundaryType; // If selected feature already has boundaryType
      // And it's not the current boundaryType, update the boundaryType and style

      if (hasBoundaryType && !isSameBoundaryType) {
        selectedFeature.setStyle(getStyles);
      }

      clearDefaultFeatureProperties(selectedFeature);
      selectedFeature.setProperties(_objectSpread({
        reference: id,
        name: label
      }, boundaryType && {
        boundaryType
      }));
      onSelect === null || onSelect === void 0 ? void 0 : onSelect(getFeatureProperties(selectedFeature, geom => getGeometryArea(geom)));
      handleSetAutofillFeatures(selectedFeature);
    };

    map.on('click', onFeatureSelect);
    return () => {
      map.un('click', onFeatureSelect);
    };
  }, [boundaryType, canSelect, getGeometryArea, getSelectedFeature, getStyles, handleSetAutofillFeatures, id, label, map, onSelect, selectLayerReference]);
  var clearAndAddFeatures = useCallback(features => {
    var _autofillLayer$getSou, _autofillLayer$getSou2;

    (_autofillLayer$getSou = autofillLayer.getSource()) === null || _autofillLayer$getSou === void 0 ? void 0 : _autofillLayer$getSou.clear();
    (_autofillLayer$getSou2 = autofillLayer.getSource()) === null || _autofillLayer$getSou2 === void 0 ? void 0 : _autofillLayer$getSou2.addFeatures(features);
  }, [autofillLayer]);
  var handleMergeFeatures = useCallback(features => {
    if (features.length) {
      var mergedGeometry = mergeGeometries(features);
      var mergedFeature = new Feature({
        geometry: parser.write(mergedGeometry)
      });
      mergedFeature.setProperties(_objectSpread({
        reference: id,
        name: label
      }, boundaryType && {
        boundaryType
      }));
      clearAndAddFeatures([mergedFeature]);
    } else {
      var _autofillLayer$getSou3;

      // Clear last remaining feature from the layer
      (_autofillLayer$getSou3 = autofillLayer.getSource()) === null || _autofillLayer$getSou3 === void 0 ? void 0 : _autofillLayer$getSou3.clear();
    }
  }, [autofillLayer, boundaryType, clearAndAddFeatures, id, label, mergeGeometries, parser]);
  var handleSelectedFeatures = useCallback(features => {
    if (canMergeFeatures) {
      handleMergeFeatures(features);
    } else {
      clearAndAddFeatures(features);
    }
  }, [canMergeFeatures, clearAndAddFeatures, handleMergeFeatures]);
  useEffect(() => {
    handleSelectedFeatures(currentBoundaryFeatures);
  }, [currentBoundaryFeatures, handleSelectedFeatures]);
  return null;
};

export default AutofillFeatures;